const CART_WEBHOOK_BEARER_TOKEN = "KnQUnm77l5lNU75H1rs5iKVEjeyt8uRM";
const THEME_ROUTES = window.theme?.routes || {};

const toJsonUrl = (url, fallback) => {
  const value = url || fallback;
  if (!value) return null;
  return value.endsWith('.js') ? value : `${value}.js`;
};

const fetchConfigDefaults = (acceptHeader = 'application/json') => ({
  method: 'POST',
  credentials: 'same-origin',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json;',
    Accept: acceptHeader,
  },
});

/**
 * Fetch cart shipping rates from Shopify Cart API using customer address.
 * GET /cart/shipping_rates.json?shipping_address[country]=...&shipping_address[province]=...&shipping_address[zip]=...
 * @param {{ country?: string, province?: string, zip?: string, address1?: string, city?: string }} address
 * @returns {Promise<{ name: string, price: string, delivery_date: string | null, source: string }[] | null>}
 */
async function fetchShippingRates(address) {
  if (!address || !address.country) return null;
  const root = (THEME_ROUTES.root_url || '/').replace(/\/$/, '') || '';
  const params = new URLSearchParams();
  params.set('shipping_address[country]', address.country);
  if (address.province) params.set('shipping_address[province]', address.province);
  if (address.zip) params.set('shipping_address[zip]', address.zip);
  if (address.address1) params.set('shipping_address[address1]', address.address1);
  if (address.city) params.set('shipping_address[city]', address.city);
  const url = `${root}/cart/shipping_rates.json?${params.toString()}`;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) return null;
  const data = await res.json();
  const rates = data.shipping_rates || null;
  console.log('[cart-webhook] fetchShippingRates result', { address: { country: address.country, province: address.province, zip: address.zip }, shipping_rates: rates });
  return rates;
}

function handleCartWebhookClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const webhookButton = target.closest('[data-cart-webhook-button]');
  if (!webhookButton) return;

  event.preventDefault();
  event.stopPropagation();

  if (webhookButton.disabled) return;

  const container = webhookButton.closest('[data-cart-webhook-container]');
  const messageEl = container?.querySelector('[data-cart-webhook-message]');
  const addressSelect = container?.querySelector('[data-cart-webhook-address-select]');
  const webhookUrl = webhookButton.dataset.webhookUrl;
  const buttonLabel = webhookButton.dataset.cartWebhookLabel || 'Submit Order To Donna Wilson';

  // Get selected address from dropdown
  let customerAddress = null;
  let allAddresses = [];

  try {
    if (container?.dataset.cartWebhookAddresses) {
      allAddresses = JSON.parse(container.dataset.cartWebhookAddresses);
    }
  } catch (error) {
    console.error('Failed to parse addresses list.', error);
  }

  if (addressSelect && allAddresses.length > 0) {
    const selectedId = parseInt(addressSelect.value, 10);
    customerAddress = allAddresses.find(addr => addr.id === selectedId) || null;
  } else {
    // Fallback to default address from data attribute
    try {
      if (webhookButton.dataset.customerAddress) {
        customerAddress = JSON.parse(webhookButton.dataset.customerAddress);
      }
    } catch (error) {
      console.error('Failed to parse customer address.', error);
    }
  }

  const setButtonLabel = (message) => {
    webhookButton.textContent = message;
  };

  webhookButton.disabled = true;
  setButtonLabel('Checking…');
  if (messageEl) {
    messageEl.textContent = '';
    messageEl.className = 'mt-2 text-sm text-scheme-text';
  }

  (async () => {
    try {
      // Check shipping rates first
      let shippingRates = null;
      try {
        shippingRates = await fetchShippingRates(customerAddress || {});
      } catch (e) {
        console.warn('Could not fetch shipping rates:', e);
      }

      if (!shippingRates || shippingRates.length === 0) {
        if (messageEl) {
          messageEl.textContent = 'ORDER MUST MEET MOV';
          messageEl.className = 'mt-2 font-heading text-heading-secondary text-center text-scheme-error';
        }
        webhookButton.disabled = false;
        setButtonLabel(buttonLabel);
        return;
      }

      // Shipping rates exist, ask for confirmation
      if (!window.confirm('Are you sure you want to send this cart?')) {
        webhookButton.disabled = false;
        setButtonLabel(buttonLabel);
        if (messageEl) {
          messageEl.textContent = '';
          messageEl.className = 'mt-2 text-sm text-scheme-text';
        }
        return;
      }

      // Confirmed, proceed with submission
      let customer = null;

      try {
        if (webhookButton.dataset.customer) {
          customer = JSON.parse(webhookButton.dataset.customer);
        }
      } catch (error) {
        console.error('Failed to parse customer data.', error);
      }

      setButtonLabel('Sending…');

      const cartUrl = toJsonUrl(THEME_ROUTES.cart_url, '/cart');
      if (!cartUrl) {
        throw new Error('Cart URL is not available.');
      }
      const cartResponse = await fetch(cartUrl, {
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) {
        throw new Error('Failed to load cart.');
      }

      const cart = await cartResponse.json();

      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${CART_WEBHOOK_BEARER_TOKEN}`,
        },
        body: JSON.stringify({
          cart,
          customer,
          customer_address: customerAddress,
          shipping_rates: shippingRates,
        }),
      });

      if (!webhookResponse.ok) {
        throw new Error('Webhook request failed.');
      }

      setButtonLabel('Cart sent.');

      const cartClearUrl = toJsonUrl(THEME_ROUTES.cart_clear_url, '/cart/clear');
      if (!cartClearUrl) {
        throw new Error('Cart clear URL is not available.');
      }
      const clearResponse = await fetch(cartClearUrl, fetchConfigDefaults());
      if (!clearResponse.ok) {
        throw new Error('Failed to clear cart.');
      }

      window.location.assign('/account');
    } catch (error) {
      console.error(error);
      setButtonLabel('Failed to send cart.');
      if (messageEl) {
        messageEl.textContent = '';
        messageEl.className = 'mt-2 text-sm text-scheme-text';
      }
    } finally {
      webhookButton.disabled = false;
      setButtonLabel(buttonLabel);
    }
  })();
}
document.addEventListener('click', handleCartWebhookClick, true);
